import { Doc } from '@dadohr/shared/domainTypes/document';
import { IExperienceTemplate } from '@dadohr/shared/domainTypes/experience';
import { useMemo } from 'react';
import {
  AdditionActionsMenuOption,
  AdditionalActionsMenu
} from '../../../../../components/AdditionalActionsMenu';
import {
  ID_TO_ROW_KEY,
  ItemSorters,
  RowsRenderer,
  ROW_HEIGHTS,
  useSortQueryParam
} from '../../../../../components/GroupableList';
import { Loader } from '../../../../../components/Loader';
import { IColumn } from '../../../../../components/Table/VirtualizedSortable';
import { TemplateStatusChip } from '../../../../../features/Experiences/components/TemplateStatusChip';
import { Section } from '../../../../../layout/Section';
import { removeDoc } from '../../../../../services/firebase';
import { formatDatePrecise, toMoment } from '../../../../../services/time';
import { JsonDialogInner } from '../../../../components/Json';
import { OrgNameLazy } from '../../../../components/OrgName';
import { useAllExperienceTemplates } from '../../../../services/experiences';
import { toFirestoreConsoleFromDoc } from '../../../../services/firebase';

type ColumnName =
  | 'orgId'
  | 'orgName'
  | 'title'
  | 'status'
  | 'tasks'
  | 'createdAt'
  | 'actions';

type D = Doc<IExperienceTemplate>;
type Column = IColumn<D, ColumnName>;

const Actions = ({ d }: { d: D }) => {
  const options: AdditionActionsMenuOption[] = useMemo(() => {
    return [
      {
        key: 'openInFirestore',
        label: 'Open in Firestore console',
        externalHref: toFirestoreConsoleFromDoc(d)
      },
      {
        key: 'inspect',
        label: 'Inspect...',
        dialog: ({ onClose }) => <JsonDialogInner onClose={onClose} data={d} />
      },

      {
        key: 'remove',
        label: 'Remove',
        onClick: () => removeDoc(d)
      }
    ];
  }, [d]);
  return <AdditionalActionsMenu options={options} />;
};

const COLUMNS: Column[] = [
  {
    key: 'orgId',
    head: () => 'Org ID',
    cell: (d) => d.data.orgId,
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 0
  },
  {
    key: 'orgName',
    head: () => 'Org Name',
    cell: (d) => <OrgNameLazy orgId={d.data.orgId} />,
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  {
    key: 'title',
    head: () => 'Title',
    cell: (d) => d.data.title,
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 2
  },
  {
    key: 'status',
    head: () => 'Status',
    cell: (d) => <TemplateStatusChip status={d.data.status} />,
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  {
    key: 'tasks',
    head: () => '# of tasks',
    cell: (d) => d.data.tasks.length,
    align: 'left',
    sortable: true,
    defaultDirection: 'desc',
    width: 100
  },
  {
    key: 'createdAt',
    head: () => 'Created at',
    cell: (d) => formatDatePrecise(toMoment(d.data.createdAt)),
    align: 'right',
    sortable: true,
    defaultDirection: 'desc',
    width: 120,
    flexGrow: 0
  },
  {
    key: 'actions',
    head: () => '',
    alternateHead: () => 'Actions',
    cell: (d) => <Actions d={d} />,
    sortable: false,
    align: 'right',
    width: 50,
    flexGrow: 0
  }
];

const SORTERS: ItemSorters<D> = {
  orgId: {
    key: 'orgId',
    items: { sort: (d) => d.data.orgId, dir: 'asc' }
  },
  name: {
    key: 'title',
    items: { sort: (d) => d.data.title, dir: 'asc' }
  },
  domain: {
    key: 'tasks',
    items: { sort: (d) => d.data.tasks.length, dir: 'asc' }
  },
  createdAt: {
    key: 'createdAt',
    items: { sort: (d) => d.data.createdAt.toMillis(), dir: 'desc' }
  }
};

const DEFAULT_SORTER = SORTERS.createdAt;

export const PageExperiencesTemplates = () => {
  const [ds, loading] = useAllExperienceTemplates();
  const [[sorter, dir], setSort] = useSortQueryParam('sort', SORTERS);
  return (
    <Section>
      {loading && <Loader height={500} />}
      {ds && (
        <RowsRenderer
          variant="contained"
          columns={COLUMNS}
          rows={ds}
          rowToKey={ID_TO_ROW_KEY}
          sorter={sorter || DEFAULT_SORTER}
          sortDirection={dir}
          renderHead={true}
          headProps={{ sticky: true }}
          onHeadClick={(c, d) => setSort([SORTERS[c.key] || DEFAULT_SORTER, d])}
          chunkSize={30}
          rootMargin="300px"
          rowHeight={ROW_HEIGHTS.dense}
          otherProps={undefined}
        />
      )}
    </Section>
  );
};
